import { ReactNode } from "react";
import * as ModalPrimitive from "@radix-ui/react-dialog";

/**
 * Enum representing available modal sizes
 */
export enum ModalSize {
  /**
   * Custom size - uses the width prop value
   */
  custom = "custom",
  /**
   * Large size - 900px width
   */
  large = "large",
  /**
   * Medium size - 600px width
   */
  medium = "medium",
  /**
   * Small size - 480px width
   */
  small = "small"
}

/**
 * Props for the Modal component
 */
export interface ModalProps {
  /**
   * Modal content
   */
  children: ReactNode;

  /**
   * Predefined size of the modal
   */
  size?: keyof typeof ModalSize;

  /**
   * Custom width of the modal (used when size is 'custom')
   * @default "95%"
   */
  width?: string | number;

  /**
   * Height of the modal
   */
  height?: string;

  /**
   * Whether to show the overlay behind the modal
   * @default true
   */
  hasOverlay?: boolean;

  /**
   * Controls whether the modal is open
   */
  isOpen: boolean;

  /**
   * Callback when modal open state changes
   */
  onOpenChange?: (open: boolean | Event) => void;

  /**
   * Callback for handling close action
   */
  handleClose?: () => void;

  /**
   * ID for the modal content element
   */
  id?: string;

  /**
   * CSS class name for the modal content
   */
  className?: string;

  /**
   * Whether to autofocus the modal content on open
   * @default false
   */
  autoFocus?: boolean;
}

/**
 * Modal namespace containing all subcomponents
 */
export interface ModalNamespace {
  /**
   * The root Dialog component
   */
  Root: typeof ModalPrimitive.Root;

  /**
   * Trigger element that opens the modal
   */
  Trigger: typeof ModalPrimitive.Trigger;

  /**
   * Portal component for rendering modals outside the DOM hierarchy
   */
  Portal: typeof ModalPrimitive.Portal;

  /**
   * Close button or trigger
   */
  Close: typeof ModalPrimitive.Close;

  /**
   * Modal background overlay
   */
  Overlay: React.FC<any>;

  /**
   * Modal content container
   */
  Content: React.FC<{
    width: number | string;
    height?: string;
    className?: string;
    [key: string]: any;
  }>;

  /**
   * Standard close button component
   */
  CloseButton: React.FC<any>;
}
